// src/features/records/services/core/mutations.js

import { functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { requireUser } from '../utils/auth';

const createRecordFn = httpsCallable(functions, 'createRecord');
const promoteRecordDraftFn = httpsCallable(functions, 'promoteRecordDraft');
const updateRecordFn = httpsCallable(functions, 'updateRecord');
const deleteRecordFn = httpsCallable(functions, 'deleteRecord');

/**
 * Create a new record directly from form data.
 */
export async function createRecord(formData, ai = null, sessionId = null, tempAttachments = null) {
  requireUser();

  try {
    const { attachments, ...contentWithoutAttachments } = formData.content;

    const recordData = {
      content: contentWithoutAttachments,
      attributes: formData.attributes,
      access: formData.access,
      links: formData.links,
    };

    if (ai) {
      recordData.ai = ai;
    }

    const { data } = await createRecordFn({
      recordData,
      sessionId,
      tempAttachments,
    });

    if (data && data.content) {
      data.content.attachments = data.migratedAttachments || attachments || [];
    }

    return data;
  } catch (error) {
    console.error('Create record failed:', error);
    throw error;
  }
}

/**
 * Promote an AI-created record draft to a full record on the backend.
 */
export async function promoteRecordDraft(draftId, overrides = null, sessionId = null, tempAttachments = null) {
  requireUser();

  if (!draftId) {
    throw new Error('draftId is required to promote a record draft');
  }

  try {
    const payload = {
      draftId,
      sessionId,
      tempAttachments,
    };

    if (overrides) {
      payload.overrides = overrides;
    }

    const { data } = await promoteRecordDraftFn(payload);
    return data;
  } catch (error) {
    console.error('Promote record draft failed:', error);
    throw error;
  }
}

/**
 * Update an existing record.
 */
export async function updateRecord(recordId, updates, sessionId = null, tempAttachments = [], onOptimisticUpdate = null) {
  requireUser();

  try {
    const updatesForBackend = { ...updates };
    if (updatesForBackend.content && updatesForBackend.content.attachments !== undefined) {
      const { attachments, ...contentWithoutAttachments } = updatesForBackend.content;
      updatesForBackend.content = contentWithoutAttachments;
    }

    if (onOptimisticUpdate) {
      const optimisticUpdates = {
        ...updates,
        metadata: {
          ...updates.metadata,
          updatedAt: new Date(),
        },
      };
      onOptimisticUpdate(recordId, optimisticUpdates);
    }

    const { data } = await updateRecordFn({
      recordId,
      updates: updatesForBackend,
      sessionId,
      tempAttachments,
    });

    return data;
  } catch (error) {
    console.error('Error updating record:', error);
    throw error;
  }
}

/**
 * Delete a record.
 */
export async function deleteRecord(recordId, onOptimisticUpdate = null) {
  requireUser();

  try {
    if (onOptimisticUpdate) {
      onOptimisticUpdate(recordId);
    }

    await deleteRecordFn({ recordId });
  } catch (error) {
    console.error('Error deleting record:', error);
    throw error;
  }
}
